import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { X, Sparkles, ArrowRight } from 'lucide-react';
import { BOOKING_URL } from '@/constants/urls';

interface OfferPopupProps {
  isOpen: boolean;
  onClose: () => void;
  offerType?: 'intro-skin' | 'custom';
  title?: string;
  description?: string;
  ctaText?: string;
  ctaLink?: string;
  badgeText?: string;
  showOnPages?: string[];
  delay?: number;
}

const OfferPopup: React.FC<OfferPopupProps> = ({
  isOpen,
  onClose,
  offerType = 'intro-skin',
  title,
  description,
  ctaText,
  ctaLink,
  badgeText,
  showOnPages = [],
  delay = 3000
}) => {
  const [shouldShow, setShouldShow] = useState(false);

  useEffect(() => {
    // Check if we should show the popup based on current page
    const currentPath = window.location.pathname;
    const shouldShowOnCurrentPage = showOnPages.length === 0 || showOnPages.includes(currentPath);
    
    // Check if user has already seen this offer on this specific page
    const offerSeenKey = `offer-${offerType}-${currentPath}-seen`;
    const hasSeenOffer = sessionStorage.getItem(offerSeenKey);
    
    if (shouldShowOnCurrentPage && !hasSeenOffer && isOpen) {
      const timer = setTimeout(() => {
        setShouldShow(true);
        sessionStorage.setItem(offerSeenKey, 'true');
      }, delay);
      
      return () => clearTimeout(timer);
    }
  }, [isOpen, offerType, showOnPages, delay]);

  const handleClose = () => {
    setShouldShow(false);
    onClose();
  };

  const handleCTAClick = () => {
    if (ctaLink) {
      window.open(ctaLink, '_blank');
    } else if (offerType === 'intro-skin') {
      window.open('/care-packages/skin-package', '_blank');
    }
    handleClose();
  };

  // Default content for intro skin offer
  const getDefaultContent = () => {
    if (offerType === 'intro-skin') {
      return {
        title: "🌟 Introductory Cosmetic Acupuncture Offer",
        description: "Start your skin transformation journey with our special introductory treatment. Perfect for first-time clients - 60 minutes of radiant skin care. Limited spaces available.",
        ctaText: "Book Your Intro Session",
        ctaLink: BOOKING_URL,
        badgeText: "Limited Spaces"
      };
    }
    return {
      title: title || "Special Offer",
      description: description || "Discover our latest offer",
      ctaText: ctaText || "Learn More",
      ctaLink: ctaLink || "#",
      badgeText: badgeText || "Special Offer"
    };
  };

  const content = getDefaultContent();

  if (!shouldShow) return null;

  return (
    <Dialog open={shouldShow} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md bg-white border-2 border-amber-200 shadow-2xl" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-200">
              {content.badgeText}
            </Badge>
          </div>
          
          <DialogTitle className="text-xl font-serif font-bold text-sage-800 mb-3">
            {content.title}
          </DialogTitle>
          
          <DialogDescription className="text-sage-700 text-center leading-relaxed">
            {content.description}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="flex items-center justify-center mb-4">
            <Sparkles className="h-8 w-8 text-amber-500 mr-2" />
            <span className="text-lg font-semibold text-amber-600">Special Pricing Available</span>
          </div>
          

          
          <div className="bg-sage-50 rounded-lg p-4 mb-4">
            <h4 className="font-semibold text-sage-800 mb-2 text-center">What's Included:</h4>
            <ul className="text-sm text-sage-700 space-y-1">
              <li>• Gentle cleanse with Founders Formula</li>
              <li>• Lymphatic Gua Sha treatment</li>
                                    <li>• Cosmetic acupuncture for radiance</li>
              <li>• Australian clay mask</li>
              <li>• LED light therapy (bonus)</li>
            </ul>
          </div>
          
          <Button
            onClick={handleCTAClick}
            className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-3 mb-3"
          >
            {content.ctaText}
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
          
          <Button
            variant="outline"
            onClick={handleClose}
            className="w-full border-sage-300 text-sage-600 hover:bg-sage-50 py-2"
          >
            Maybe Later
          </Button>
          
          <p className="text-xs text-sage-500 text-center mt-3">
            Limited spaces available •Book Now - offer expires September
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OfferPopup; 